/**
 * Product Card Inline Layout
 *
 * @package
 */

import PropTypes from 'prop-types';
import { __, sprintf } from '@wordpress/i18n';
import { decodeHtmlEntities } from '../helpers/decodeHtmlEntities';
import { getImageAltText } from '../helpers/getImageAltText';
import InstallationBadge from './InstallationBadge';
import QuantitySelector from './QuantitySelector';

const ProductCardInline = ( {
	productData,
	isSelected,
	isLocked,
	isDisabled,
	isLoading,
	onToggle,
	onInfoClick,
	formattedPrice,
	installationHours,
	allowQuantity,
	quantity,
	onQuantityChange,
} ) => {
	const handleKeyDown = e => {
		if ( e.key === 'Enter' || e.key === ' ' ) {
			e.preventDefault();
			if ( ! isLocked && ! isDisabled ) {
				onToggle();
			}
		}
	};

	const productTitle = decodeHtmlEntities( productData.title?.rendered ) || __( 'Product', 'at-vehicle-configurator' );

	return (
		<div
			className={ `configurator-product-card configurator-product-card-inline ${ isSelected ? 'selected' : '' } ${ isLocked ? 'locked' : '' } ${ isDisabled ? 'disabled' : '' } ${ isLoading ? 'checkbox-loading' : '' } checkbox-mode` }
			role="button"
			tabIndex={ isLocked || isDisabled ? -1 : 0 }
			onClick={ () => ! isLocked && ! isDisabled && onToggle() }
			onKeyDown={ handleKeyDown }
			aria-label={ `${ isLocked ? __( 'Locked: ', 'at-vehicle-configurator' ) : '' }${ isDisabled ? __( 'Disabled: ', 'at-vehicle-configurator' ) : '' }${ isSelected ? __( 'Selected: ', 'at-vehicle-configurator' ) : __( 'Select ', 'at-vehicle-configurator' ) }${ productTitle }` }
			aria-pressed={ isSelected }
			aria-disabled={ isLocked || isDisabled }
		>
			<button
				type="button"
				className="configurator-product-card-info-icon"
				onClick={ onInfoClick }
				aria-label={ sprintf(
					/* translators: %s: product name */
					__( 'View information about %s', 'at-vehicle-configurator' ),
					productTitle
				) }
			>
				<span className="screen-reader-text">{ __( 'Information', 'at-vehicle-configurator' ) }</span>
				i
			</button>
			{ productData.featured_media_url && (
				<div className="configurator-product-card-image-wrapper">
					<img
						src={ productData.featured_media_url }
						alt={ getImageAltText( productData, 'Product' ) }
						loading="lazy"
						decoding="async"
					/>
				</div>
			) }
			<div className="configurator-product-card-content">
				<h4>{ productTitle }</h4>
				{ formattedPrice && (
					<div className="configurator-product-price">
						{ formattedPrice }
						<InstallationBadge hours={ installationHours } />
					</div>
				) }
				{ allowQuantity && isSelected && (
					<QuantitySelector
						quantity={ quantity }
						onChange={ units => onQuantityChange( units ) }
						productTitle={ productTitle }
						disabled={ isLocked }
					/>
				) }
			</div>
		</div>
	);
};

ProductCardInline.propTypes = {
	productData: PropTypes.shape( {
		title: PropTypes.shape( {
			rendered: PropTypes.string,
		} ),
		featured_media_url: PropTypes.string,
	} ).isRequired,
	isSelected: PropTypes.bool.isRequired,
	isLocked: PropTypes.bool.isRequired,
	isDisabled: PropTypes.bool.isRequired,
	isLoading: PropTypes.bool,
	onToggle: PropTypes.func.isRequired,
	onInfoClick: PropTypes.func.isRequired,
	formattedPrice: PropTypes.string,
	installationHours: PropTypes.number,
	allowQuantity: PropTypes.bool,
	quantity: PropTypes.number,
	onQuantityChange: PropTypes.func,
};

ProductCardInline.defaultProps = {
	isLoading: false,
	formattedPrice: null,
	installationHours: 0,
	allowQuantity: false,
	quantity: 1,
	onQuantityChange: null,
};

export default ProductCardInline;
